import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MagicLinkCallbackPage } from './pages/MagicLinkCallbackPage';
import { EventsPage } from './pages/EventsPage';
import { ApiCallsPage } from './pages/ApiCallsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { PaymentsPage } from './pages/PaymentsPage';
import { CentresPage } from './pages/CentresPage';
import { ProductsPage } from './pages/ProductsPage';
import { UsersPage } from './pages/UsersPage';
import { IContactPage } from './pages/IContactPage';
import App from './App';

type Route =
    | 'home'
    | 'events'
    | 'api-calls'
    | 'invoices'
    | 'payments'
    | 'centres'
    | 'products'
    | 'users'
    | 'icontact'
    | 'auth-callback';

function getRoute(): Route {
    const path = window.location.pathname;
    if (path === '/auth/callback') {
        return 'auth-callback';
    }
    if (path === '/events') {
        return 'events';
    }
    if (path === '/api-calls') {
        return 'api-calls';
    }
    if (path === '/invoices') {
        return 'invoices';
    }
    if (path === '/payments') {
        return 'payments';
    }
    if (path === '/centres') {
        return 'centres';
    }
    if (path === '/products') {
        return 'products';
    }
    if (path === '/users') {
        return 'users';
    }
    if (path === '/icontact') {
        return 'icontact';
    }
    return 'home';
}

/**
 * Home page component that redirects teachers to the invoices page
 */
function HomePage() {
    const { role } = useAuth();

    useEffect(() => {
        if (role === 'teacher') {
            window.history.replaceState({}, '', '/invoices');
            window.dispatchEvent(new PopStateEvent('popstate'));
        }
    }, [role]);

    // Teachers will be redirected, show nothing while redirecting
    if (role === 'teacher') {
        return null;
    }

    return <App />;
}

export function AppRouter() {
    const [route, setRoute] = useState<Route>(getRoute());

    useEffect(() => {
        const handlePopState = () => {
            setRoute(getRoute());
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const renderRoute = () => {
        switch (route) {
            case 'auth-callback':
                return <MagicLinkCallbackPage />;
            case 'events':
                return (
                    <ProtectedRoute>
                        <EventsPage />
                    </ProtectedRoute>
                );
            case 'api-calls':
                return (
                    <ProtectedRoute>
                        <ApiCallsPage />
                    </ProtectedRoute>
                );
            case 'invoices':
                return (
                    <ProtectedRoute>
                        <InvoicesPage />
                    </ProtectedRoute>
                );
            case 'payments':
                return (
                    <ProtectedRoute>
                        <PaymentsPage />
                    </ProtectedRoute>
                );
            case 'centres':
                return (
                    <ProtectedRoute>
                        <CentresPage />
                    </ProtectedRoute>
                );
            case 'products':
                return (
                    <ProtectedRoute>
                        <ProductsPage />
                    </ProtectedRoute>
                );
            case 'users':
                return (
                    <ProtectedRoute>
                        <UsersPage />
                    </ProtectedRoute>
                );
            case 'icontact':
                return (
                    <ProtectedRoute>
                        <IContactPage />
                    </ProtectedRoute>
                );
            default:
                return (
                    <ProtectedRoute>
                        <HomePage />
                    </ProtectedRoute>
                );
        }
    };

    return <AuthProvider>{renderRoute()}</AuthProvider>;
}
